import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { createPortal } from 'react-dom';
import RequiredMark from '@/shein-platform/components/RequiredMark';
import SheinImagePreviewLightbox from '@/shein-platform/modules/products/components/SheinImagePreviewLightbox';
import { nextTemplatePriority } from '@/shein-platform/utils/sheinTemplatePriority';
import {
  conflictToastMessage,
  isTemplateVersionConflictError,
  parseTemplateVersion,
} from '@/shein-platform/utils/sheinTemplateVersionGuard';
import TemplateVersionConflictMark from '@/shein-platform/modules/products/components/TemplateVersionConflictMark';
import { sheinPlatformAPI } from '@/utils/api';
import { adminAPI } from '@/utils/adminApi';
import { showToast } from '@/utils/toast';
import SheinUploadSourceMenu from '@/shein-platform/components/SheinUploadSourceMenu';
import LibraryModalContent from '@/pages/ipe/workbench/modals/LibraryModalContent';
import { imageSpaceRecordToUploadFile } from '@/shein-platform/utils/sheinImageSpacePick';
import { appendUniqueImageSpaceRows } from '@/pages/ipe/workbench/utils/imageSpacePageFetch';
import { useFixedAspectCropGate } from '@/components/image/useFixedAspectCropGate';
import '@/styles/ipe/workbench/IpeModalUnified.css';

function fetchPhotoDetail(officialMode, shopId, templateId, templateScope) {
  if (officialMode) {
    return adminAPI.getOfficialSheinPhotoTemplate(templateId);
  }
  return sheinPlatformAPI.getPhotoTemplate(shopId, templateId, { scope: templateScope });
}

function fetchPhotoList(officialMode, shopId, params) {
  if (officialMode) {
    return adminAPI.listOfficialSheinPhotoTemplates(params);
  }
  return sheinPlatformAPI.listPhotoTemplates(shopId, params);
}

function savePhotoTemplateApi(officialMode, shopId, templateId, body) {
  if (officialMode) {
    return templateId
      ? adminAPI.updateOfficialSheinPhotoTemplate(templateId, body)
      : adminAPI.createOfficialSheinPhotoTemplate(body);
  }
  return templateId
    ? sheinPlatformAPI.updatePhotoTemplate(shopId, templateId, body)
    : sheinPlatformAPI.createPhotoTemplate(shopId, body);
}

const PHOTO_MAX_PER_CHANNEL = 5;
const PHOTO_MAX_BYTES = 10 * 1024 * 1024;
const PHOTO_MAX_EDGE = 8000;
const PHOTO_ACCEPT = '.png,.jpg,.jpeg,image/png,image/jpeg';

function newPhotoItemId() {
  return `photo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function isAllowedPhotoFile(file) {
  if (!file) return false;
  const name = String(file.name || '').toLowerCase();
  const type = String(file.type || '').toLowerCase();
  return (
    type === 'image/png'
    || type === 'image/jpeg'
    || type === 'image/jpg'
    || name.endsWith('.png')
    || name.endsWith('.jpg')
    || name.endsWith('.jpeg')
  );
}

function readImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth || img.width, height: img.naturalHeight || img.height });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(i18n.t('photoLib.readSizeFailed', { ns: 'workbench' })));
    };
    img.src = url;
  });
}

async function validatePhotoFile(file) {
  if (!isAllowedPhotoFile(file)) throw new Error(i18n.t('photoLib.typeOnly', { ns: 'workbench' }));
  if (file.size > PHOTO_MAX_BYTES) {
    throw new Error(i18n.t('photoLib.fileTooBig', { ns: 'workbench', size: (file.size / 1024 / 1024).toFixed(1) }));
  }
  const { width, height } = await readImageDimensions(file);
  if (width > PHOTO_MAX_EDGE || height > PHOTO_MAX_EDGE) {
    throw new Error(i18n.t('photoLib.edgeTooBig', { ns: 'workbench', max: PHOTO_MAX_EDGE, width, height }));
  }
  if (width < 900 || height < 900) {
    throw new Error(i18n.t('photoLib.edgeTooSmall', { ns: 'workbench', width, height }));
  }
}

function pickPhotoDisplayUrl(item = {}) {
  return String(
    item?.previewUrlDisplay
    || item?.originalUrlDisplay
    || item?.viewUrl
    || item?.displayUrl
    || item?.previewUrl
    || item?.originalUrl
    || item?.imageUrl
    || '',
  ).trim();
}

function mapPhotoItems(list) {
  return (Array.isArray(list) ? list : []).map((item, index) => {
    const originalUrl = String(item?.originalUrl || item?.imageUrl || '').trim();
    return {
      id: String(item?.id || `photo-${index}-${Math.random().toString(36).slice(2, 6)}`),
      name: item?.name || i18n.t('photoLib.photoN', { ns: 'workbench', index: index + 1 }),
      originalUrl,
      previewUrl: pickPhotoDisplayUrl(item) || originalUrl,
    };
  }).filter((item) => item.originalUrl);
}

/**
 * 新建/编辑实拍图库：按款号匹配 + 优先级 + 上传包装/本体原图 → 保存（不转平台 URL）。
 * 类目/SPU/模板级自动规则已废弃；自动绑定由店铺合规开关 + 款号匹配完成。
 */
export default function PhotoTemplateCreateModal({
  open,
  shopId,
  templateId = null,
  templateScope = 'PERSONAL',
  officialMode = false,
  shopType = '',
  onClose,
  onSaved,
}) {
  const { t } = useTranslation('workbench');
  const isEdit = Boolean(templateId);
  const [name, setName] = useState('');
  const [styleNo, setStyleNo] = useState('');
  const [remark, setRemark] = useState('');
  const [sortOrder, setSortOrder] = useState(0);
  const [packageItems, setPackageItems] = useState([]);
  const [bodyItems, setBodyItems] = useState([]);
  const [uploadingChannel, setUploadingChannel] = useState(null);
  const [dragOverChannel, setDragOverChannel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [previewSrc, setPreviewSrc] = useState('');
  const [previewAlt, setPreviewAlt] = useState('');
  const packageInputRef = useRef(null);
  const bodyInputRef = useRef(null);
  const packageUploadAnchorRef = useRef(null);
  const bodyUploadAnchorRef = useRef(null);
  const imageSpaceRowsRef = useRef([]);
  const [uploadMenuChannel, setUploadMenuChannel] = useState(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryPickChannel, setLibraryPickChannel] = useState(null);
  const [librarySelection, setLibrarySelection] = useState([]);
  const [templateVersion, setTemplateVersion] = useState(null);
  const [versionConflict, setVersionConflict] = useState(false);
  const [reloadingVersion, setReloadingVersion] = useState(false);
  const { ensureAspectFile, cropModal, cropping } = useFixedAspectCropGate({
    title: t('photoLib.cropTitle'),
    hint: t('photoLib.cropHint'),
    maxEdge: PHOTO_MAX_EDGE,
    maxBytes: PHOTO_MAX_BYTES,
    minCropEdge: 900,
  });

  useEffect(() => {
    if (!open) return;
    setName('');
    setStyleNo('');
    setRemark('');
    setSortOrder(0);
    setPackageItems([]);
    setBodyItems([]);
    setUploadingChannel(null);
    setDragOverChannel(null);
    setSaving(false);
    setDetailLoading(false);
    setPreviewSrc('');
    setPreviewAlt('');
    if (packageInputRef.current) packageInputRef.current.value = '';
    if (bodyInputRef.current) bodyInputRef.current.value = '';
    setUploadMenuChannel(null);
    setLibraryOpen(false);
    setLibraryPickChannel(null);
    setLibrarySelection([]);
    imageSpaceRowsRef.current = [];
    setTemplateVersion(null);
    setVersionConflict(false);
    setReloadingVersion(false);
  }, [open, templateId]);

  useEffect(() => {
    if (!open || isEdit || (!officialMode && !shopId)) return undefined;
    let cancelled = false;
    fetchPhotoList(officialMode, shopId, {
      scope: templateScope,
      page: 1,
      pageSize: 100,
      ...(officialMode ? { shopType } : {}),
    }).then((res) => {
      if (!cancelled) setSortOrder(nextTemplatePriority(res?.items));
    }).catch(() => {
      if (!cancelled) setSortOrder(0);
    });
    return () => { cancelled = true; };
  }, [open, isEdit, shopId, officialMode, templateScope, shopType]);

  useEffect(() => {
    if (!open || !templateId || (!officialMode && !shopId)) return undefined;
    let cancelled = false;
    setDetailLoading(true);
    fetchPhotoDetail(officialMode, shopId, templateId, templateScope)
      .then((res) => {
        if (cancelled) return;
        const item = res?.item || res || {};
        const payload = item.payload && typeof item.payload === 'object' ? item.payload : {};
        setName(String(item.name || '').trim());
        setStyleNo(String(item.styleNo || '').trim());
        setRemark(String(item.remark || '').trim());
        setSortOrder(Number.isFinite(Number(item.sortOrder)) ? Number(item.sortOrder) : 0);
        setTemplateVersion(parseTemplateVersion(item.version));
        setVersionConflict(false);
        setPackageItems(mapPhotoItems(payload.packageImages));
        setBodyItems(mapPhotoItems(payload.bodyImages));
      })
      .catch((e) => {
        if (cancelled) return;
        showToast(e?.message || t('photoLib.loadTemplateFailed'), 'error');
        onClose?.();
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, shopId, templateId, templateScope, officialMode, onClose, t]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving && !uploadingChannel && !previewSrc) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, uploadingChannel, previewSrc, onClose]);

  const rememberImageSpaceRows = useCallback((rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    imageSpaceRowsRef.current = appendUniqueImageSpaceRows(imageSpaceRowsRef.current, rows);
  }, []);

  if (!open) return null;

  const busy = saving || detailLoading || Boolean(uploadingChannel) || cropping;
  const canUpload = officialMode || Boolean(shopId);
  const totalCount = packageItems.length + bodyItems.length;

  const openPreview = (item) => {
    const src = item?.previewUrl || item?.originalUrl || '';
    if (!src) return;
    setPreviewSrc(src);
    setPreviewAlt(item?.name || t('photoLib.previewAlt'));
  };

  const channelRemainSlots = (channel) => {
    const count = channel === 'package' ? packageItems.length : bodyItems.length;
    return Math.max(0, PHOTO_MAX_PER_CHANNEL - count);
  };

  const uploadOriginal = async (file) => {
    if (!isAllowedPhotoFile(file)) throw new Error(i18n.t('photoLib.typeOnly', { ns: 'workbench' }));
    const aspectOk = await ensureAspectFile(file);
    await validatePhotoFile(aspectOk);
    const res = officialMode
      ? await adminAPI.storeOfficialSheinPhotoTemplateOriginal(aspectOk)
      : await sheinPlatformAPI.storePhotoTemplateOriginal(shopId, aspectOk);
    const originalUrl = String(res?.originalUrl || '').trim();
    if (!originalUrl) {
      throw new Error(t('photoLib.saveOriginalFailed'));
    }
    return {
      id: newPhotoItemId(),
      name: res?.name || aspectOk.name || file.name || t('photoLib.photoName'),
      originalUrl,
      previewUrl: pickPhotoDisplayUrl(res) || originalUrl,
    };
  };

  const pickFiles = async (channel, fileList) => {
    if (!canUpload) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    const remainSlots = channelRemainSlots(channel);
    const channelLabel = channel === 'package' ? t('photoLib.package') : t('photoLib.body');
    if (remainSlots <= 0) {
      showToast(t('photoLib.channelMax', { channel: channelLabel, max: PHOTO_MAX_PER_CHANNEL }), 'warning');
      return;
    }
    const picked = files.slice(0, remainSlots);
    if (picked.length < files.length) {
      showToast(t('photoLib.channelOver', { count: picked.length, channel: channelLabel, max: PHOTO_MAX_PER_CHANNEL }), 'warning');
    }
    setUploadingChannel(channel);
    try {
      const uploaded = [];
      for (const file of picked) {
        uploaded.push(await uploadOriginal(file));
      }
      if (channel === 'package') {
        setPackageItems((prev) => [...prev, ...uploaded]);
      } else {
        setBodyItems((prev) => [...prev, ...uploaded]);
      }
    } catch (e) {
      const msg = String(e?.message || '');
      if (msg.includes('已取消裁剪')) {
        showToast(t('photoLib.cropCancelledUpload'), 'info');
      } else {
        showToast(msg || t('photoLib.uploadFailed'), 'error');
      }
    } finally {
      setUploadingChannel(null);
      if (channel === 'package' && packageInputRef.current) packageInputRef.current.value = '';
      if (channel === 'body' && bodyInputRef.current) bodyInputRef.current.value = '';
    }
  };

  const pickFromImageSpace = async (channel, records) => {
    const picked = (Array.isArray(records) ? records : []).filter(Boolean);
    if (!picked.length) return;
    const remainSlots = channelRemainSlots(channel);
    const channelLabel = channel === 'package' ? t('photoLib.package') : t('photoLib.body');
    if (remainSlots <= 0) {
      showToast(t('photoLib.channelMax', { channel: channelLabel, max: PHOTO_MAX_PER_CHANNEL }), 'warning');
      return;
    }
    const batch = picked.slice(0, remainSlots);
    if (batch.length < picked.length) {
      showToast(t('photoLib.overPicked', { count: batch.length }), 'warning');
    }
    setUploadingChannel(channel);
    try {
      const uploaded = [];
      for (const image of batch) {
        const file = await imageSpaceRecordToUploadFile(image);
        uploaded.push(await uploadOriginal(file));
      }
      if (channel === 'package') {
        setPackageItems((prev) => [...prev, ...uploaded]);
      } else {
        setBodyItems((prev) => [...prev, ...uploaded]);
      }
    } catch (e) {
      const msg = String(e?.message || '');
      if (msg.includes('已取消裁剪')) {
        showToast(t('photoLib.cropCancelledPick'), 'info');
      } else {
        showToast(msg || t('photoLib.pickFailed'), 'error');
      }
    } finally {
      setUploadingChannel(null);
    }
  };

  const enableImageSpacePick = !officialMode;

  const openUploadMenu = (channel) => {
    if (busy || !canUpload) return;
    if (!enableImageSpacePick) {
      if (channel === 'package') packageInputRef.current?.click();
      else bodyInputRef.current?.click();
      return;
    }
    setUploadMenuChannel(channel);
  };

  const handleLibraryClose = () => {
    setLibrarySelection([]);
    setLibraryOpen(false);
    setLibraryPickChannel(null);
  };

  const handleLibraryToggle = (imageId) => {
    const id = String(imageId);
    const channel = libraryPickChannel;
    const remain = channel ? channelRemainSlots(channel) : PHOTO_MAX_PER_CHANNEL;
    setLibrarySelection((prev) => {
      if (prev.some((x) => String(x) === id)) {
        return prev.filter((x) => String(x) !== id);
      }
      if (prev.length >= remain) {
        showToast(t('photoLib.remainMax', { count: remain }), 'warning');
        return prev;
      }
      return [...prev, id];
    });
  };

  const handleLibraryConfirm = async (positionArray) => {
    const channel = libraryPickChannel;
    if (!channel) return;
    const ids = (positionArray || []).filter((x) => x != null && x !== '');
    if (ids.length === 0) {
      showToast(t('photoLib.pickOne'), 'info');
      return;
    }
    const remain = channelRemainSlots(channel);
    const pickedIds = ids.slice(0, remain);
    const records = pickedIds
      .map((id) => imageSpaceRowsRef.current.find((row) => String(row?.id) === String(id)))
      .filter(Boolean);
    if (records.length === 0) {
      showToast(t('photoLib.invalidImages'), 'warning');
      return;
    }
    handleLibraryClose();
    await pickFromImageSpace(channel, records);
  };

  const removeItem = (channel, id) => {
    if (channel === 'package') {
      setPackageItems((prev) => prev.filter((item) => item.id !== id));
    } else {
      setBodyItems((prev) => prev.filter((item) => item.id !== id));
    }
  };

  const applyPhotoTemplateItem = (item) => {
    const payload = item.payload && typeof item.payload === 'object' ? item.payload : {};
    setName(String(item.name || '').trim());
    setStyleNo(String(item.styleNo || '').trim());
    setRemark(String(item.remark || '').trim());
    setSortOrder(Number.isFinite(Number(item.sortOrder)) ? Number(item.sortOrder) : 0);
    setTemplateVersion(parseTemplateVersion(item.version));
    setPackageItems(mapPhotoItems(payload.packageImages));
    setBodyItems(mapPhotoItems(payload.bodyImages));
  };

  const reloadTemplateFromServer = async () => {
    if (!templateId || (!officialMode && !shopId)) return;
    setReloadingVersion(true);
    try {
      const res = await fetchPhotoDetail(officialMode, shopId, templateId, templateScope);
      applyPhotoTemplateItem(res?.item || res || {});
      setVersionConflict(false);
      showToast(t('photoLib.reloaded'), 'success');
    } catch (e) {
      showToast(e?.message || t('photoLib.reloadFailed'), 'error');
    } finally {
      setReloadingVersion(false);
    }
  };

  const submit = async () => {
    if (!officialMode && !shopId) {
      showToast(t('returnList.pickShop'), 'error');
      return;
    }
    const styleNoValue = String(styleNo || '').trim();
    if (!styleNoValue) {
      showToast(t('photoLib.needStyleNo'), 'error');
      return;
    }
    const title = String(name || '').trim();
    if (!title) {
      showToast(t('photoLib.needName'), 'error');
      return;
    }
    if (totalCount <= 0) {
      showToast(t('photoLib.needPhoto'), 'error');
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: title,
        styleNo: styleNoValue,
        remark: String(remark || '').trim() || undefined,
        // 清空历史类目/SPU；匹配仅按款号
        productTypeId: '',
        categoryId: '',
        categoryPathLabel: '',
        spuName: '',
        spuTitle: '',
        autoRuleEnabled: true,
        sortOrder: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0,
        payload: {
          packageImages: packageItems.map((item) => ({
            originalUrl: item.originalUrl,
            previewUrl: item.originalUrl,
            name: item.name,
          })),
          bodyImages: bodyItems.map((item) => ({
            originalUrl: item.originalUrl,
            previewUrl: item.originalUrl,
            name: item.name,
          })),
        },
      };
      if (officialMode) {
        body.shopType = shopType;
      }
      if (!isEdit && !officialMode) {
        body.scope = templateScope;
      }
      if (isEdit && templateVersion != null) {
        body.version = templateVersion;
      }
      const res = await savePhotoTemplateApi(officialMode, shopId, isEdit ? templateId : null, body);
      if (res?.item && parseTemplateVersion(res.item.version) != null) {
        setTemplateVersion(parseTemplateVersion(res.item.version));
      }
      setVersionConflict(false);
      showToast(isEdit ? t('photoLib.updated') : t('photoLib.createdTpl'), 'success');
      onSaved?.();
      onClose?.();
    } catch (e) {
      if (isEdit && isTemplateVersionConflictError(e)) {
        showToast(conflictToastMessage([{ type: 'photo' }]), 'warning', 3000);
        setVersionConflict(true);
      } else {
        showToast(e?.message || (isEdit ? t('photoLib.updateFailed') : t('photoLib.createFailed')), 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const renderChannel = (channel, title, items, inputRef, anchorRef) => {
    const channelBusy = uploadingChannel === channel;
    const channelDragOver = dragOverChannel === channel;
    const remainSlots = channelRemainSlots(channel);
    return (
      <div className="spo-img-upload spo-photo-bind__channel">
        <div className="spo-img-upload__head">
          <span className="spo-field__label">{title}</span>
          <span className="spo-img-upload__hint">{t('photoLib.countUnit', { current: items.length, max: PHOTO_MAX_PER_CHANNEL })}</span>
        </div>
        <div className="spo-img-upload__grid">
          {items.map((item) => (
            <div key={item.id} className="spo-img-upload__thumb">
              <button
                type="button"
                className="spo-img-upload__thumb-hit"
                title={t('photoLib.previewClick')}
                onClick={() => openPreview(item)}
              >
                <img src={item.previewUrl || item.originalUrl} alt={item.name || title} />
              </button>
              <button
                type="button"
                className="spo-img-upload__preview"
                onClick={() => openPreview(item)}
              >
                {t('photoLib.preview')}
              </button>
              <button
                type="button"
                className="spo-img-upload__remove"
                aria-label={t('photoLib.remove', { title })}
                disabled={busy}
                onClick={() => removeItem(channel, item.id)}
              >
                ×
              </button>
            </div>
          ))}
          {remainSlots > 0 ? (
            <button
              ref={anchorRef}
              type="button"
              className={[
                'spo-img-upload__drop',
                channelDragOver ? 'spo-img-upload__drop--over' : '',
                channelBusy ? 'spo-img-upload__drop--busy' : '',
              ].filter(Boolean).join(' ')}
              disabled={busy || !canUpload}
              aria-busy={channelBusy}
              aria-haspopup={enableImageSpacePick ? 'menu' : undefined}
              onClick={() => openUploadMenu(channel)}
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!busy && canUpload) setDragOverChannel(channel);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!busy && canUpload) setDragOverChannel(channel);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragOverChannel((prev) => (prev === channel ? null : prev));
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragOverChannel(null);
                if (busy || !canUpload) return;
                pickFiles(channel, e.dataTransfer?.files);
              }}
            >
              {channelBusy ? (
                <span className="spo-img-upload__busy">
                  <span className="spo-img-upload__spinner" />
                  <span className="spo-img-upload__busy-text">{t('photoLib.uploading')}</span>
                </span>
              ) : (
                <>
                  <span className="spo-img-upload__plus">+</span>
                  <span className="spo-img-upload__sub">{channelDragOver ? t('photoLib.dropUpload') : t('photoLib.clickUpload')}</span>
                </>
              )}
            </button>
          ) : null}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={PHOTO_ACCEPT}
          multiple
          hidden
          disabled={busy || !canUpload}
          onChange={(e) => pickFiles(channel, e.target.files)}
        />
      </div>
    );
  };

  return createPortal(
    <div className="spo-batch-modal spo-cert-modal" role="presentation">
      <button
        type="button"
        className="spo-batch-modal__backdrop"
        aria-label={t('moduleShell.close')}
        onClick={() => { if (!busy) onClose?.(); }}
      />
      <div
        className="spo-batch-modal__panel spo-cert-modal__panel spo-photo-bind-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spo-photo-tpl-create-title"
      >
        <div className="spo-batch-modal__head">
          <h2 id="spo-photo-tpl-create-title">
            {isEdit
              ? (officialMode ? t('photoLib.editOfficial') : t('photoLib.editLib'))
              : (officialMode ? t('photoLib.createOfficial') : t('photoLib.createLib'))}
          </h2>
          <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" onClick={onClose} disabled={busy}>
            {t('moduleShell.close')}
          </button>
        </div>
        <div className="spo-batch-modal__body spo-cert-modal__body">
          {detailLoading ? (
            <p className="spo-wizard__inline-hint">{t('photoLib.loadingTemplate')}</p>
          ) : null}
          <div className="spo-wizard__form spo-template-create-form">
            <label className="spo-field spo-field--full">
              <span className="spo-field__label">{t('productTemplate.colStyleNo')} <RequiredMark /></span>
              <input
                className="spo-input"
                value={styleNo}
                disabled={busy}
                maxLength={128}
                placeholder={t('photoLib.styleNoPh')}
                onChange={(e) => setStyleNo(e.target.value)}
              />
            </label>
            <label className={`spo-field spo-field--full${versionConflict ? ' spo-field--version-conflict' : ''}`}>
              <span className="spo-field__label">
                {t('productTemplate.colName')} <RequiredMark />
                <TemplateVersionConflictMark
                  active={versionConflict}
                  label={t('photoLib.updatedMark')}
                  reloading={reloadingVersion}
                  onReload={reloadTemplateFromServer}
                />
              </span>
              <input
                className="spo-input"
                value={name}
                disabled={busy}
                maxLength={128}
                placeholder={t('photoLib.namePh')}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="spo-field spo-field--full">
              <span className="spo-field__label">{t('productTemplate.colRemark')}</span>
              <input
                className="spo-input"
                value={remark}
                disabled={busy}
                maxLength={200}
                placeholder={t('photoLib.optional')}
                onChange={(e) => setRemark(e.target.value)}
              />
            </label>
            <label className="spo-field">
              <span className="spo-field__label">{t('productTemplate.colPriority')}</span>
              <input
                className="spo-input"
                type="number"
                step={1}
                value={sortOrder}
                disabled={busy}
                onChange={(e) => {
                  const next = Number(e.target.value);
                  setSortOrder(Number.isFinite(next) ? next : 0);
                }}
              />
              <span className="spo-wizard__inline-hint">
                {t('photoLib.priorityHint')}
              </span>
            </label>
          </div>
          <div className="spo-photo-bind__channels">
            {renderChannel('package', t('photoLib.packageChannel'), packageItems, packageInputRef, packageUploadAnchorRef)}
            {renderChannel('body', t('photoLib.bodyChannel'), bodyItems, bodyInputRef, bodyUploadAnchorRef)}
          </div>
          <p className="spo-photo-bind__tips-text spo-template-create-photo-rule">
            {officialMode ? t('photoLib.uploadHintOfficialLocal') : t('photoLib.uploadHint')}
          </p>
        </div>
        <div className="spo-batch-modal__foot">
          <button type="button" className="spo-btn spo-btn--secondary spo-btn--sm" onClick={onClose} disabled={busy}>
            {t('shop.cancel')}
          </button>
          <button
            type="button"
            className="spo-btn spo-btn--primary spo-btn--sm"
            onClick={submit}
            disabled={busy || (!officialMode && !shopId) || totalCount === 0}
          >
            {saving ? t('tiktokPublish.saving') : uploadingChannel ? t('photoLib.uploadingImages') : (isEdit ? t('photoLib.saveChanges') : t('shop.save'))}
          </button>
        </div>
      </div>
      <SheinImagePreviewLightbox
        src={previewSrc}
        alt={previewAlt}
        onClose={() => {
          setPreviewSrc('');
          setPreviewAlt('');
        }}
      />
      {enableImageSpacePick ? (
        <>
          <SheinUploadSourceMenu
            open={uploadMenuChannel === 'package'}
            anchorRef={packageUploadAnchorRef}
            onClose={() => setUploadMenuChannel(null)}
            disabled={busy}
            onPickLocal={() => packageInputRef.current?.click()}
            onPickImageSpace={() => {
              setUploadMenuChannel(null);
              setLibraryPickChannel('package');
              setLibraryOpen(true);
            }}
          />
          <SheinUploadSourceMenu
            open={uploadMenuChannel === 'body'}
            anchorRef={bodyUploadAnchorRef}
            onClose={() => setUploadMenuChannel(null)}
            disabled={busy}
            onPickLocal={() => bodyInputRef.current?.click()}
            onPickImageSpace={() => {
              setUploadMenuChannel(null);
              setLibraryPickChannel('body');
              setLibraryOpen(true);
            }}
          />
          <LibraryModalContent
            isOpen={libraryOpen}
            onClose={handleLibraryClose}
            images={[]}
            selection={librarySelection}
            onToggle={handleLibraryToggle}
            maxSelection={libraryPickChannel ? channelRemainSlots(libraryPickChannel) : PHOTO_MAX_PER_CHANNEL}
            onConfirm={handleLibraryConfirm}
            onImagesAppended={rememberImageSpaceRows}
            uploadOriginSource="upload"
            showToast={showToast}
            deferUsageBump
            selectAllLabel={t('photoLib.selectLoaded')}
          />
        </>
      ) : null}
      {cropModal}
    </div>,
    document.body,
  );
}
